import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../api';
import { AuthContext } from '../context/AuthContext';
import { ABG } from './abg';
import API_BASE_URL from '../apiConfig';
import './DepositWithdrawAllPage.css';
import { commonIcons } from './assetImports';
import { useTranslation } from 'react-i18next';

const DepositWithdrawAllPage = ({ isTelegramMiniApp }) => {
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const { t, ready } = useTranslation('depositWithdrawAll');
    const [transactions, setTransactions] = useState([]);
    const [loadingTransactions, setLoadingTransactions] = useState(false);
    const [error, setError] = useState(null);

    const renderAvatar = (name, photoUrl, customAvatar) => {
         if (customAvatar) {
        return <img src={customAvatar} alt={`${name}'s Custom Avatar`} className="deposit-avatar-image" />;
        }
        if (photoUrl) {
            return <img src={photoUrl} alt={`${name}'s Avatar`} className="deposit-avatar-image" />;
        }
        const initial = name?.charAt(0)?.toUpperCase() || 'U';
        const avatarStyle = ABG(name);
        return <div className="deposit-avatar-placeholder" style={avatarStyle}>{initial}</div>;
    };

    const getCurrencyIcon = (currency) => {
        if (currency === 'token') {
            return <img src={commonIcons.tokenYellow} alt={t('tokens')} className="depositWithdrawMain-currency-icon token" />;
        }
        return isTelegramMiniApp 
            ? <img src={commonIcons.starIcon} alt={t('stars')} className="depositWithdrawMain-currency-icon" />
            : <img src={commonIcons.usdtIcon} alt="USDT" className="depositWithdrawMain-currency-icon" />;
    };

    const getHistoryCurrencyIcon = (currency) => {
        switch (currency) {
            case 'stars':
                return <img src={commonIcons.starIcon} alt={t('stars')} className="depositWithdraw-currency-icon depositWithdraw-currency-icon-stars" />;
            case 'token':
                return <img src={commonIcons.tokenYellow} alt={t('tokens')} className="depositWithdraw-currency-icon depositWithdraw-currency-icon-token" />;
            default:
                return <img src={commonIcons.usdtIcon} alt="USDT" className="depositWithdraw-currency-icon depositWithdraw-currency-icon-usdt" />;
        }
    };

    const getBalance = () => {
        return isTelegramMiniApp 
            ? user?.wallets['stars'] || 0
            : user?.wallets['usdt']?.toFixed(2) || '0.00';
    };

    const fetchTransactions = async () => {
        try {
            setLoadingTransactions(true);
            setError(null);
            const response = await apiRequest(`${API_BASE_URL}/api/transactions/${user._id}`, {
                method: 'GET',
                credentials: 'include',
            });

            if (response.ok) {
                const data = await response.json();
                setTransactions(data.transactions);
            } else {
                setError('error');
            }
        } catch (err) {
            setError('error');
        } finally {
            setLoadingTransactions(false);
        }
    };

    useEffect(() => {
        if (user?._id) {
            fetchTransactions();
        }
    }, [user]);

    const filteredTransactions = transactions
        .filter(transaction => transaction.status === 'completed')
        .filter(transaction => 
            (transaction.type === 'deposit' || 
                transaction.type === 'exchange' ||
                transaction.type === 'withdrawal') &&
            (isTelegramMiniApp 
                ? transaction.currency === 'stars' || transaction.currency === 'token'
                : transaction.currency === 'usdt' || transaction.currency === 'token')
        )
        .sort((a, b) => new Date(b.date) - new Date(a.date));

    const groupTransactionsByDate = () => {
        if (!ready) return {};
        
        const months = t('months', { returnObjects: true });
        
        return filteredTransactions.reduce((acc, transaction) => {
            const dateObj = new Date(transaction.date);
            const monthIndex = dateObj.getMonth();
            const monthName = months[monthIndex] || '';
            const dateKey = `${dateObj.getDate()} ${monthName}`;

            const formattedDate = `${String(dateObj.getDate()).padStart(2, '0')}.${String(
                dateObj.getMonth() + 1
            ).padStart(2, '0')}.${dateObj.getFullYear()}, ${String(dateObj.getHours()).padStart(
                2,
                '0'
            )}:${String(dateObj.getMinutes()).padStart(2, '0')}:${String(
                dateObj.getSeconds()
            ).padStart(2, '0')}`;

            if (!acc[dateKey]) acc[dateKey] = [];
            acc[dateKey].push({
                ...transaction,
                formattedDate
            });
            return acc;
        }, {});
    };

    const handleDepositClick = () => {
        if (isTelegramMiniApp) {
            navigate('/depositStars');
        } else {
            navigate('/deposit');
        }
    };

    const handleWithdrawClick = () => {
        if (isTelegramMiniApp) {
            navigate('/withdrawStars');
        } else {
            navigate('/withdraw');
        }
    };

    if (!ready) {
        return (
            <div className="deposit-withdraw-container">
                <div className="deposit-loading-message">{t('loading')}</div>
            </div>
        );
    }

    const groupedTransactions = groupTransactionsByDate();

    return (
        <div className="deposit-withdraw-container">
            {/* Avatar and Balance Section */}
            <div className="deposit-user-wrapper">
                <div className="deposit-user-area">
                    <div className="deposit-user-avatar">
                        <div className="deposit-avatar-gradient-border"></div>
                        {renderAvatar(user?.nickname, user?.photoUrl, user?.customAvatar)}
                    </div>

                    <div className="deposit-user-info">
                        <div className="deposit-balance-display">
                            {getCurrencyIcon(isTelegramMiniApp ? 'stars' : 'usdt')}
                            <span className="deposit-balance-amount">{getBalance()}</span>
                        </div>
                    </div>

                    <div className="deposit-action-buttons">
                        <button 
                            className="depositAll-button"
                            onClick={handleDepositClick}
                        >
                            {t('deposit')}
                        </button>
                        <button 
                            className="withdrawAll-button"
                            onClick={handleWithdrawClick}
                        >
                            {t(isTelegramMiniApp ? 'getReward' : 'withdraw')}
                        </button>
                    </div>
                </div>
            </div>

            {/* Transaction History Section */}
            <div className="deposit-history-section">
                <h2 className="deposit-history-title">{t('transactionHistory')}</h2>

                <div className="deposit-transaction-list">
                    {loadingTransactions ? (
                        <div className="deposit-loading-message">{t('loading')}</div>
                    ) : error ? (
                        <div className="deposit-error-message">{t(error)}</div>
                    ) : Object.keys(groupedTransactions).length > 0 ? (
                        <div className="deposit-transaction-list-animate">
                            {Object.entries(groupedTransactions).map(([date, transactions]) => (
                                <div key={date} className="deposit-transaction-group">
                                    <h3 className="deposit-transaction-group-date">{date}</h3>
                                    {transactions.map((transaction) => (
                                        <div key={transaction._id} className="deposit-transaction-item">
                                            <div className="deposit-transaction-info">
                                                <div className="deposit-transaction-type">
                                                    {t(`types.${transaction.type}`, { 
                                                        defaultValue: transaction.type.charAt(0).toUpperCase() + transaction.type.slice(1) 
                                                    })}
                                                </div>
                                                <div className="deposit-transaction-date">
                                                    {transaction.formattedDate}
                                                </div>
                                            </div>
                                            <div className="deposit-transaction-amount">
                                                <span className={`deposit-amount-value ${transaction.type}`}>
                                                    {(transaction.type === 'deposit' || transaction.type === 'exchange') ? '+' : '-'}
                                                    {transaction.currency === 'usdt' 
                                                        ? transaction.amount.toFixed(2)
                                                        : transaction.amount}
                                                </span>
                                                {getHistoryCurrencyIcon(transaction.currency)}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="deposit-empty-message">{t('noTransactions')}</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DepositWithdrawAllPage;